/**
 * Markdown Utilities
 *
 * Utility functions for processing markdown content.
 */

import type { TableOfContentItem } from '../types/manual';

/**
 * Generate a URL-safe anchor ID from heading text
 * Supports Chinese characters and special chars
 */
export const generateAnchorId = (text: string): string => {
  // Remove HTML tags
  let cleaned = text.replace(/<[^>]*>/g, '');

  // Convert to lowercase and remove special characters
  cleaned = cleaned
    .toLowerCase()
    .replace(/[^\w\u4e00-\u9fff\u3400-\u4dbf\-\s]/g, '') // Keep alphanumeric, Chinese, hyphens, spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove duplicate hyphens
    .trim();

  // Add prefix to avoid conflicts with other IDs
  return `heading-${cleaned}`;
};

/**
 * Extract headings from markdown content and generate table of contents
 */
export const extractHeadings = (content: string): TableOfContentItem[] => {
  const headings: TableOfContentItem[] = [];

  // Match markdown headings: # Heading, ## Heading, etc.
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length;
    const title = match[2].trim();
    const id = generateAnchorId(title);

    headings.push({
      id,
      title,
      level,
      offset: match.index,
    });
  }

  return headings;
};

/**
 * Smooth scroll to a heading by element ID
 */
export const scrollToHeading = (elementId: string, offset = 100): void => {
  const element = document.getElementById(elementId);

  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

/**
 * Check if an image exists by sending a HEAD request
 */
export const checkImageExists = async (src: string): Promise<boolean> => {
  try {
    const response = await fetch(src, { method: 'HEAD' });
    const contentType = response.headers.get('content-type');
    return response.ok && (contentType?.startsWith('image/') ?? false);
  } catch (error) {
    console.error('Error checking image:', error);
    return false;
  }
};

/**
 * Throttle function to limit execution rate
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastExecTime = 0;

  return (...args: Parameters<T>) => {
    const currentTime = Date.now();
    const timeSinceLastExec = currentTime - lastExecTime;

    if (timeSinceLastExec > delay) {
      func(...args);
      lastExecTime = currentTime;
    } else {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        func(...args);
        lastExecTime = Date.now();
      }, delay - timeSinceLastExec);
    }
  };
};

/**
 * Parse image path and convert relative paths to absolute
 */
export const normalizeImagePath = (src: string, _basePath: string): string => {
  // If already absolute or starts with /, return as is
  if (src.startsWith('/') || src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }

  // Handle relative paths
  if (src.startsWith('./') || src.startsWith('../')) {
    // Resolve relative to basePath
    const normalizedPath = src.replace(/\.\.\//g, '').replace(/^\.\//, '');
    return `/docs/delivery/${normalizedPath}`;
  }

  // If src already contains 'screenshots/', don't duplicate it
  if (src.startsWith('screenshots/')) {
    return `/docs/delivery/${src}`;
  }

  // Default: assume it's relative to screenshots directory
  return `/docs/delivery/screenshots/${src}`;
};
